import * as React from 'react';
import {
    AcceptIcon,
    AddIcon,
    Button,
    CloseIcon,
    Dialog,
    Flex,
    FlexItem,
    Image,
    Loader,
    Text
} from "@fluentui/react-northstar";
import SharepointPicker from "../Utils/SharepointPicker";
import ImagePickerModal from "./ImagePickerModal";
import ImagePlaceholder from "../../assets/imagePlaceholder.png";
import {ImageGallery} from '../../Interfaces/ImageGallery';
import languageObject from "../../translations";

/**
 * ImageBrowserDialog props
 */
interface IImageBrowserDialogProps {
    show: boolean,
    selectedImage: ImageGallery | undefined,
    availableImages: Array<ImageGallery>,
    handleSelectImage: (image: ImageGallery) => void,
    onHide: () => void,
    onImagesUpdated: (images: Array<ImageGallery>) => Promise<void>,
}

/**
 * ImageBrowserDialog state
 */
interface IImageBrowserDialogState {
    loaded: boolean,
    selectedImage: string,
    showImageUploadDialog: boolean
}

/**
 * ImageBrowserDialog component
 */
class ImageBrowserDialog extends React.Component<IImageBrowserDialogProps, IImageBrowserDialogState> {
    /**
     * Constructor
     */
    constructor(props: IImageBrowserDialogProps) {
        super(props);

        this.state = {
            loaded: true,
            selectedImage: "",
            showImageUploadDialog: false,
        }
    }

    /**
     * Component initialization
     */
    componentDidMount() {
    }

    /**
     * Allow component to render
     * @param nextProps next props
     * @param nextState next state
     */
    shouldComponentUpdate(nextProps: Readonly<IImageBrowserDialogProps>, nextState: Readonly<IImageBrowserDialogState>): boolean {
        return (this.props.show || nextProps.show) && (this.state.loaded || nextState.loaded);
    }

    /**
     * When component has updated
     * @param prevProps previous props
     * @param prevState previous state
     */
    componentDidUpdate(prevProps: Readonly<IImageBrowserDialogProps>, prevState: Readonly<IImageBrowserDialogState>) {
        // When opening dialog
        if (!prevProps.show && this.props.show) {
            if (this.props.selectedImage?.imageLink !== this.state.selectedImage)
                this.setState({selectedImage: this.props.selectedImage?.imageLink ?? ""});
        }
    }

    /**
     * Handle image selection
     */
    handleSelection = () => {
        const image = this.props.availableImages.find(img => img.imageLink === this.state.selectedImage);
        if (!!image) this.props.handleSelectImage(image);
    }

    /**
     * Render component
     */
    render() {
        // Footer text
        const footerText = !this.state.loaded || !this.state.selectedImage ?
            languageObject.translate("PleaseSelectImage")
            :
            <>
                {languageObject.translate("YouHaveSelectedImage") + " "}
                <Text weight={"semibold"} color={"brand"}>
                    {this.state.selectedImage.substring(this.state.selectedImage.lastIndexOf("/") + 1)}
                </Text>
            </>;

        // Image preview source
        const imgPreviewSrc = !!this.state.selectedImage ?
            this.props.availableImages.find(img => img.imageLink === this.state.selectedImage)?.content
            ?? ImagePlaceholder
            :
            ImagePlaceholder

        return <>
            <Dialog
                className={"test_dialog"}
                styles={{width: "800px", height: "510px"}}
                open={this.props.show}
                content={this.state.loaded ?
                    <Flex fill column styles={{paddingTop: "10px"}}>
                        <FlexItem align={"end"}>
                            <Button
                                styles={{marginBottom: "15px"}}
                                icon={<AddIcon outline/>}
                                onClick={() => this.setState({showImageUploadDialog: true})}
                                content={languageObject.translate("UploadImage")}/>
                        </FlexItem>
                        <Flex fill className={"overflow-hidden"} gap={"gap.medium"} vAlign={"center"}>
                            <SharepointPicker
                                fixScrollBarLeft
                                styles={{height: "300px"}}
                                items={this.props.availableImages.map(img => img.imageLink)}
                                handleSelect={(item => this.setState({selectedImage: item}))}
                                selectedFile={this.props.selectedImage}
                                noItemsContent={
                                    <Flex fill column id={"sharepoint_picker"} hAlign={"center"} vAlign={"center"}>
                                        <Text size={"largest"} content={languageObject.translate("NoImage")}/>
                                    </Flex>
                                }
                            />
                            <Image src={imgPreviewSrc}
                                   styles={{minWidth: "300px", maxWidth: "300px", maxHeight: "100%"}}/>
                        </Flex>
                    </Flex>
                    :
                    <Flex fill hAlign={"center"} vAlign={"center"}>
                        <Loader/>
                    </Flex>
                }
                header={languageObject.translate("SelectImage")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: this.props.onHide,
                }}
                footer={
                    <Flex fill vAlign={"end"} gap={"gap.medium"}>
                        <Text weight={"semibold"} styles={{marginBottom: "5px"}} content={footerText} truncated/>
                        <FlexItem push>
                            <Button
                                primary
                                disabled={!this.state.selectedImage || !this.state.loaded}
                                content={languageObject.translate("Select")}
                                icon={<AcceptIcon outline/>}
                                onClick={this.handleSelection}
                            />
                        </FlexItem>
                    </Flex>
                }
            />
            <ImagePickerModal
                show={this.state.showImageUploadDialog}
                onClose={() => this.setState({showImageUploadDialog: false})}
                images={this.props.availableImages}
                onImagesUpdated={async images => await this.props.onImagesUpdated(images)}
            />
        </>
    }
}

export default ImageBrowserDialog;